import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useSession } from './auth';
import { sendPush } from './usePush';
import type { Enrollment, Submission, InboxSubmission, ProofType } from '../types';

// Pass userId to view a specific family member's enrollment (for "act as kid" flows).
// Defaults to the signed-in user.
export function useEnrollment(patchId: string | undefined, userIdOverride?: string | null) {
  const { user } = useSession();
  const targetUserId = userIdOverride ?? user?.id;
  return useQuery<Enrollment | null>({
    queryKey: ['enrollment', patchId, targetUserId],
    enabled: !!patchId && !!targetUserId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('enrollments')
        .select('*')
        .eq('patch_id', patchId!)
        .eq('user_id', targetUserId!)
        .maybeSingle();
      if (error) throw error;
      return (data as Enrollment) ?? null;
    },
  });
}

export function useEnrollmentSubmissions(enrollmentId: string | undefined) {
  return useQuery<Submission[]>({
    queryKey: ['enrollment-submissions', enrollmentId],
    enabled: !!enrollmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .eq('enrollment_id', enrollmentId!)
        .is('deleted_at', null)
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Submission[];
    },
  });
}

export function useFamilyMembers() {
  const { profile } = useSession();
  return useQuery({
    queryKey: ['family-members', profile?.family_id],
    enabled: !!profile?.family_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, member_color, avatar_url, role, is_child')
        .eq('family_id', profile!.family_id!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        display_name: string;
        member_color: string;
        avatar_url: string | null;
        role: string;
        is_child: boolean;
      }>;
    },
  });
}

export function usePendingInbox() {
  const { user } = useSession();
  return useQuery<InboxSubmission[]>({
    queryKey: ['inbox', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      // Inbox is now family-wide: every leader sees every pending submission
      // (RLS scopes to the family). The Boy-Scout-leader model: any leader
      // can sign off — first one to act wins.
      const { data, error } = await supabase
        .from('submissions')
        .select(
          `*,
           submitter:profiles!submissions_submitter_id_fkey(id, display_name, member_color, avatar_url),
           task:patch_tasks(id, text, icon, points),
           enrollment:enrollments(id, patch:patches(id, slug, name))`
        )
        .eq('status', 'pending')
        .is('deleted_at', null)
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as InboxSubmission[];
    },
  });
}

export function useEnroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { patchId: string; userId?: string | null }) => {
      const { data, error } = await supabase.rpc('enroll_in_patch', {
        p_patch_id: args.patchId,
        p_user_id: args.userId ?? null,
      });
      if (error) throw error;
      return data as Enrollment;
    },
    onSuccess: () => {
      // Refetch all enrollment queries — the page may show different
      // user contexts (?as=kid) that aren't tied to one queryKey.
      queryClient.invalidateQueries({ queryKey: ['enrollment'] });
    },
  });
}

export function useSubmitProof() {
  const queryClient = useQueryClient();
  const { profile } = useSession();
  return useMutation({
    mutationFn: async (args: {
      enrollmentId: string;
      taskId: string;
      proofType: ProofType;
      caption: string;
      mediaUrl?: string;
    }) => {
      const { data, error } = await supabase.rpc('submit_proof', {
        p_enrollment_id: args.enrollmentId,
        p_task_id: args.taskId,
        p_proof_type: args.proofType,
        p_caption: args.caption,
        p_media_url: args.mediaUrl ?? null,
      });
      if (error) throw error;
      // Notify all leaders/admins in the family. The first one to act wins.
      const { data: leaders } = await supabase
        .from('profiles')
        .select('id')
        .in('role', ['admin', 'leader'])
        .neq('id', profile?.id ?? '');
      const leaderIds = (leaders ?? []).map((l: { id: string }) => l.id);
      if (leaderIds.length > 0) {
        void sendPush({
          userIds: leaderIds,
          title: 'Proof to verify',
          body: `${profile?.display_name ?? 'Someone'} submitted proof — tap to review.`,
          url: '/inbox',
          tag: 'proof-submitted',
        });
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['feed-events'] });
    },
  });
}

// Promote/demote a family member's role (admin only). Cannot touch admins.
export function useSetMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { memberId: string; role: 'leader' | 'member' }) => {
      const { data, error } = await supabase.rpc('set_member_role', {
        p_member_id: args.memberId,
        p_role: args.role,
      });
      if (error) throw error;
      return data as { id: string; role: 'leader' | 'member' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-members'] });
    },
  });
}

export function useDeleteSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { submissionId: string }) => {
      const { data, error } = await supabase.rpc('delete_submission', {
        p_submission_id: args.submissionId,
      });
      if (error) throw error;
      const result = data as { submission_id: string; media_url: string | null };
      if (result?.media_url) {
        const path = pathFromPublicUrl(result.media_url, 'proof-media');
        if (path) {
          await supabase.storage.from('proof-media').remove([path]);
        }
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

function pathFromPublicUrl(url: string, bucket: string): string | null {
  const marker = `/storage/v1/object/public/${bucket}/`;
  const idx = url.indexOf(marker);
  if (idx === -1) return null;
  return decodeURIComponent(url.slice(idx + marker.length));
}

export function useApproveSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      submissionId: string;
      note: string;
      submitterId?: string;
      submitterName?: string;
      patchName?: string;
      patchSlug?: string;
      familyMemberIds?: string[];
    }) => {
      const { data, error } = await supabase.rpc('approve_submission', {
        p_submission_id: args.submissionId,
        p_note: args.note,
      });
      if (error) throw error;
      const result = data as { newly_earned: boolean; new_points: number; goal_points: number };
      const url = args.patchSlug ? `/patches/${args.patchSlug}` : '/sash';
      const patchLabel = args.patchName ?? 'a patch';
      if (args.submitterId) {
        void sendPush({
          userIds: [args.submitterId],
          title: result.newly_earned ? `${patchLabel} earned!` : 'Proof approved',
          body: result.newly_earned
            ? `You earned ${patchLabel}. Tap to celebrate.`
            : `Your proof on ${patchLabel} was approved.`,
          url,
          tag: 'proof-approved',
        });
      }
      if (result.newly_earned && args.familyMemberIds && args.familyMemberIds.length > 0) {
        const others = args.familyMemberIds.filter((id) => id !== args.submitterId);
        if (others.length > 0) {
          void sendPush({
            userIds: others,
            title: 'Patch earned',
            body: `${args.submitterName ?? 'A family member'} earned ${patchLabel}.`,
            url,
            tag: `earned-${args.submissionId}`,
          });
        }
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['enrollment'] });
      queryClient.invalidateQueries({ queryKey: ['enrollment-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['my-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['feed-events'] });
    },
  });
}

export function useRejectSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { submissionId: string; note: string }) => {
      const { error } = await supabase.rpc('reject_submission', {
        p_submission_id: args.submissionId,
        p_note: args.note,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['enrollment-submissions'] });
    },
  });
}
